/**
 * The global provider is responsible for loading all the configs(tenant, brand, user, activeSubscription)
 * into a single global object that can be used throughout the app. This should be loaded as part of
 * the site loading, so all pages have access. The global provider leverages the onsnapshot, so
 * changes made in db realtime are synchronized with the provider. Here is the global object structure:
 * {
 * tenant: {tenant object},
 * brand: {brand object},
 * user: {user object},
 * activeSub: {active subscription object}
 * }
 *
 * This provider also provides the functions to load (loadConfig) and watch the configs for changes and to
 * unsubscribe (unsub) from the changes.
 */

import { useCallback, useReducer, useRef } from "react";
import { db } from "../../firebaseClient";
import Context from "./Context";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  limit,
} from "firebase/firestore";

const UPDATE = "UPDATE";
const UPDATE_WITH_SIGIL_CONFIG = "UPDATE_WITH_SIGIL_CONFIG";
const SET_CONFIG_READY = "SET_CONFIG_READY";
const RESET = "RESET";
const SET_CONFIG_NOT_READY = "SET_CONFIG_NOT_READY";
const SIGIL_CONFIG_TENANT = "sigil";

const STRIPE_COLLECTION = `stripe_customers`;
const CONFIG_COLLECTION = `configs`;

const initialState = { configReady: false };

function reducer(state, action) {
  const { type, prop, document } = action;

  switch (type) {
    case SET_CONFIG_NOT_READY:
      return { ...state, configReady: false };

    case SET_CONFIG_READY:
      return { ...state, configReady: true };

    case RESET:
      return initialState;

    case UPDATE:
      if (document)
        return { ...state, [prop]: { ...document.data(), ref: document.ref } };
      return { ...state, [prop]: null };

    case UPDATE_WITH_SIGIL_CONFIG: {
      if (!document) return { ...state, [prop]: null };

      const changed = document.data();
      const isSigil = changed.tenant === SIGIL_CONFIG_TENANT;

      const tenantConfigPropName = `primary-${prop}`;
      const sigilConfigPropName = `sigil-${prop}`;
      const nameForStoredDoc = isSigil
        ? sigilConfigPropName
        : tenantConfigPropName;

      const nextState = {
        ...state,
        [nameForStoredDoc]: { ...changed, ref: document.ref },
      };

      const baseSigilProps = isSigil
        ? changed.props
        : nextState[sigilConfigPropName]?.props;
      const tenantProps = isSigil
        ? nextState[tenantConfigPropName]?.props
        : changed.props;

      if (!nextState[tenantConfigPropName]) {
        return nextState;
      }

      return {
        ...nextState,
        [prop]: {
          ...nextState[tenantConfigPropName],
          props: { ...(baseSigilProps || {}), ...(tenantProps || {}) },
        },
      };
    }

    default:
      throw new Error("No matching type for Global Provider reducer");
  }
}

const Provider = ({ children }) => {
  const [globalConfig, dispatch] = useReducer(reducer, initialState);

  const watchersRef = useRef([]);
  const loadedKeyRef = useRef(null);

  const clearWatchers = useCallback(() => {
    watchersRef.current.forEach((fn) => {
      try {
        fn?.();
      } catch {}
    });
    watchersRef.current = [];
    loadedKeyRef.current = null;
    dispatch({ type: RESET });
  }, []);

  const loadConfig = useCallback(
    (user, tenant) => {
      const key = `${tenant}::${user?.email || "unknown"}`;
      if (loadedKeyRef.current === key) return;

      clearWatchers();
      dispatch({ type: SET_CONFIG_NOT_READY });

      try {
        watchersRef.current.push(
          onSnapshot(
            doc(db, CONFIG_COLLECTION, `tenant::${tenant}`),
            (docSnap) => {
              dispatch({
                type: UPDATE_WITH_SIGIL_CONFIG,
                prop: "tenant",
                document: docSnap,
              });
              dispatch({ type: SET_CONFIG_READY });
            },
            (error) => {
              console.error("GlobalConfig tenant listener error", error);
              dispatch({ type: SET_CONFIG_READY });
            },
          ),
        );

        // Sigil base tenant config
        watchersRef.current.push(
          onSnapshot(
            doc(db, CONFIG_COLLECTION, `tenant::sigil`),
            (docSnap) => {
              if (docSnap.exists()) {
                dispatch({
                  type: UPDATE_WITH_SIGIL_CONFIG,
                  prop: "tenant",
                  document: docSnap,
                });
              }
            },
            (error) => {
              console.error("GlobalConfig sigil listener error", error);
            },
          ),
        );

        // Brand config
        watchersRef.current.push(
          onSnapshot(
            doc(db, CONFIG_COLLECTION, `brand::${tenant}::${tenant}`),
            (docSnap) => {
              dispatch({ type: UPDATE, prop: "brand", document: docSnap });
            },
            (error) => {
              console.error("GlobalConfig brand listener error", error);
              dispatch({ type: UPDATE, prop: "brand", document: null });
            },
          ),
        );

        // User config
        watchersRef.current.push(
          onSnapshot(
            doc(db, CONFIG_COLLECTION, `user::${tenant}::${user.email}`),
            (docSnap) => {
              dispatch({ type: UPDATE, prop: "user", document: docSnap });
            },
            (error) => {
              console.error("GlobalConfig user listener error", error);
              dispatch({ type: UPDATE, prop: "user", document: null });
            },
          ),
        );

        // Active subscription
        const activeSubQuery = query(
          collection(db, `${STRIPE_COLLECTION}/${tenant}/subscriptions`),
          where("status", "==", "active"),
          limit(1),
        );

        watchersRef.current.push(
          onSnapshot(
            activeSubQuery,
            (qSnap) => {
              dispatch({
                type: UPDATE,
                prop: "activeSub",
                document: qSnap.empty ? null : qSnap.docs[0],
              });
            },
            (error) => {
              console.error("GlobalConfig activeSub listener error", error);
              dispatch({ type: UPDATE, prop: "activeSub", document: null });
            },
          ),
        );

        loadedKeyRef.current = key;
      } catch (e) {
        console.log(JSON.stringify(e));
        dispatch({ type: SET_CONFIG_READY });
      }
    },
    [clearWatchers],
  );

  const unSub = useCallback(() => {
    clearWatchers();
  }, [clearWatchers]);

  return (
    <Context.Provider value={{ loadConfig, globalConfig, unSub }}>
      {children}
    </Context.Provider>
  );
};

export default Provider;
